/**
 * An entry counts as "filled" (journaled / backed up) when the user has done
 * ANY of the following on that date:
 *
 *   • edited passage, principle, or practice
 *   • added or answered a prayer request / praise
 *   • ticked a "prayed today" check-in
 *   • added or completed a task (stored inside dailyEntries)
 *   • logged a milestone  (milestones[].date === dateKey)
 *   • started or checked-in to a challenge (challenges[].startDate or checkIns[])
 *   • completed a challenge (completedChallenges[].completedAt starts with dateKey)
 *
 * All parameters beyond `saved` are optional — pass only what you have access
 * to at the call site; missing ones simply won't contribute to the check.
 *
 * @param {object}  saved                  – persisted dailyEntries[dateKey]
 * @param {Array}   [prayerRequests]       – global 'prayerRequests' store
 * @param {string}  [dateKey]              – YYYY-MM-DD for the date being checked
 * @param {object}  [prayedDays]           – 'prayerCheckins' store { [dateKey]: { [reqId]: true } }
 * @param {Array}   [milestones]           – global 'milestones' store
 * @param {Array}   [challenges]           – global 'challenges' store (active)
 * @param {Array}   [completedChallenges]  – global 'completedChallenges' store
 */
export const isEntryFilled = (
  saved,
  prayerRequests      = [],
  dateKey             = '',
  prayedDays          = {},
  milestones          = [],
  challenges          = [],
  completedChallenges = [],
) => {
  // Field edits / notes
  if (saved?.hasEdits) return true;
  // FIX (BUG-2): Added all 5P fields so a day is correctly marked filled when
  // any 5P content exists — not just practiceNotes/praises/prayer.
  if (saved?.passage?.trim())       return true;
  if (saved?.principle?.trim())     return true;
  if (saved?.practice?.trim())      return true;
  if (saved?.praise?.trim())        return true;
  if (saved?.practiceNotes?.trim()) return true;
  if (saved?.praises?.trim())       return true;
  if (saved?.prayer?.trim())        return true;

  // Tasks stored inside the daily entry
  if (saved?.tasks?.length > 0)       return true;
  if (saved?.customTasks?.length > 0) return true;

  if (!dateKey) return false;

  // Prayer requests added or answered on this date
  if (prayerRequests.some(r => r.dateAdded === dateKey || r.dateAnswered === dateKey)) return true;

  // "Prayed today" check-ins
  if (prayedDays[dateKey] && Object.keys(prayedDays[dateKey]).length > 0) return true;

  // Milestones logged on this date (covers various field name conventions)
  if (milestones.some(m =>
    m.date === dateKey ||
    m.createdAt?.startsWith(dateKey) ||
    m.achievedAt?.startsWith(dateKey) ||
    m.completedAt?.startsWith(dateKey)
  )) return true;

  // Active challenges: started or checked-in on this date
  if (challenges.some(c => c.startDate === dateKey || (c.checkIns ?? []).includes(dateKey))) return true;

  // Completed challenges finished on this date
  if (completedChallenges.some(c => c.completedAt?.startsWith(dateKey))) return true;

  return false;
};